import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import somaBg from '../assets/soma_hero.png';
import mockupDash from '../assets/landing_mockup_dashboard.png';
import mockupMob from '../assets/landing_mockup_mobile.png';
import { ArrowRight, Activity, BrainCircuit, Watch, Flame, HeartPulse, ChevronDown, Users, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NousLogo from './NousLogo';
import GlobalFooter from './GlobalFooter';

export default function SomaLandingPage() {
  const navigate = useNavigate();
  const bentoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('bento-reveal');
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.bento-item');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="theme-soma" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#07090f', color: 'white', overflowX: 'hidden' }}>
      <Helmet>
        <title>NousSoma — Analítica Inteligente y Readiness para Atletas</title>
        <meta name="description" content="Decodifica tu cuerpo. Monitoriza tu TSB, VFC y estado de recuperación. Sincronización transparente con Strava, Garmin y Apple Watch para llevarte al pico de forma." />
      </Helmet>
      
      {/* NAVEGACIÓN SUPERIOR */}
      <nav style={{ position: 'fixed', top: 0, width: '100%', padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 100, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <NousLogo size="nav" showSlogan={false} variant="soma" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <button onClick={() => navigate('/')} style={{ background: 'transparent', color: '#888', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
            ← Volver al Hub
          </button>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="mobile-padding hero-section hero-bg" style={{
        position: 'relative',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {/* Fondo Cinemático */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${somaBg})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />

        {/* Overlay desktop: degradado horizontal */}
        <div className="hide-on-mobile" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 100%)', zIndex: 1 }} />

        {/* Overlay móvil: cinemático de abajo a arriba */}
        <div className="show-on-mobile" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1 }} />
        <div className="show-on-mobile" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80%', background: 'linear-gradient(to top, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.85) 50%, transparent 100%)', zIndex: 2 }} />

        {/* Flotantes eliminados a petición */}

        <div className="hero-content-box" style={{ position: 'relative', zIndex: 3, maxWidth: '900px' }}>
          <h1 className="hero-title" style={{ background: 'linear-gradient(135deg, #fff 0%, #FC4C02 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            <span className="show-on-mobile">Tu cuerpo habla.<br/><span style={{ background: 'linear-gradient(135deg, #FC4C02, #ff7b00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Nous lo traduce.</span></span>
            <span className="hide-on-mobile">NousSoma. El cuerpo en ejecución.</span>
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.4rem)',
            color: 'rgba(255,255,255,0.8)',
            lineHeight: 1.6,
            marginBottom: '1.5rem',
            maxWidth: '800px',
            fontWeight: 400
          }}>
            <span className="show-on-mobile">Sincroniza tu Garmin o Apple Watch y obtén tu Readiness, TSB y diagnóstico IA cada semana.</span>
            <span className="hide-on-mobile">Nous (Mente) + Soma (El cuerpo, la biología humana).<br/>El motor de tu rendimiento. Analiza el impacto fisiológico, asimila las cargas de entrenamiento y alcanza tu pico de forma guiado por la ciencia.</span>
          </p>
          <div className="show-on-mobile">
            <button
              onClick={() => navigate('/app')}
              style={{
                background: 'linear-gradient(135deg, #FC4C02 0%, #ff7b00 100%)',
                color: 'white',
                border: 'none',
                padding: '0.9rem 2rem',
                borderRadius: '40px',
                fontWeight: 800,
                fontSize: '1rem',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(252, 76, 2, 0.4)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Empezar →
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '2.5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
          zIndex: 3,
          opacity: 0.6,
          animation: 'bounce 2s infinite'
        }}>
          <span style={{ fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#fff', fontWeight: 700 }}>Explora</span>
          <ChevronDown size={24} color="#fff" />
        </div>
      </section>

      {/* ESTILOS DE ANIMACIÓN INYECTADOS */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0) translateX(-50%); }
          40% { transform: translateY(-10px) translateX(-50%); }
          60% { transform: translateY(-5px) translateX(-50%); }
        }
      `}</style>

      {/* FEATURE BLOCK 1: Dashboard / IA */}
      <section className="feature-block" style={{ display: 'flex', minHeight: '100vh', background: '#000', alignItems: 'center', overflow: 'hidden' }}>
        <div className="feature-image parallax-bg" style={{ flex: 1, height: '100vh', backgroundImage: `url(${mockupDash})`, backgroundSize: 'cover', backgroundPosition: 'center right' }}></div>
        <div className="feature-text" style={{ flex: 1, padding: '10%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <h2 style={{ fontSize: 'clamp(3rem, 6vw, 5rem)', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '1.5rem', background: 'linear-gradient(135deg, #fff 0%, #fc4c02 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Decodifica<br/>tu cuerpo.
          </h2>
          <p style={{ fontSize: '1.4rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, fontWeight: 300 }}>
            Descubre patrones de fatiga que ni tú sabías que tenías. Tu <strong style={{color: '#fff'}}>TSB (Training Stress Balance)</strong> y tu <strong style={{color: '#fff'}}>VFC</strong> analizados al milímetro para evitar el sobreentrenamiento y llevarte al pico de forma exacto el día de la carrera.
          </p>
        </div>
      </section>

      {/* FEATURE BLOCK 2: Lifestyle / Mobile */}
      <section className="feature-block reverse" style={{ display: 'flex', minHeight: '100vh', background: '#050505', alignItems: 'center', overflow: 'hidden' }}>
        <div className="feature-text" style={{ flex: 1, padding: '10%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <h2 style={{ fontSize: 'clamp(3rem, 6vw, 5rem)', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '1.5rem', background: 'linear-gradient(135deg, #fff 0%, #ff7b00 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Tu entrenador,<br/>en tu muñeca.
          </h2>
          <p style={{ fontSize: '1.4rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, fontWeight: 300 }}>
            Sincronización invisible. Sal a correr con tu <strong style={{color: '#fff'}}>Garmin, Apple Watch o Coros</strong>, y nosotros hacemos el resto. Toda la potencia del ecosistema conectado trabajando en segundo plano para darte respuestas, no solo números.
          </p>
        </div>
        <div className="feature-image parallax-bg" style={{ flex: 1, height: '100vh', backgroundImage: `url(${mockupMob})`, backgroundSize: 'cover', backgroundPosition: 'center left' }}></div>
      </section>

      {/* MÉTRICAS PREMIUM */}
      <section style={{ padding: '8rem 5%', background: '#000' }}>
        <div style={{ maxWidth: '1300px', margin: '0 auto' }}>

          {/* HEADER */}
          <div style={{ marginBottom: '5rem' }}>
            <p style={{ color: '#fc4c02', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '1rem' }}>INTELIGENCIA BIOLÓGICA</p>
            <h2 style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: 0, lineHeight: 1 }}>
              Tu cuerpo habla.<br/>
              <span style={{ background: 'linear-gradient(135deg, #fc4c02, #ff7b00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Nous lo traduce.</span>
            </h2>
          </div>

          {/* GRID 2 cols → 1 col en móvil */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }} className="bento-soma-grid">

            {/* CARD 1: TSB */}
            <div style={{ background: 'rgba(252,76,2,0.04)', border: '1px solid rgba(252,76,2,0.15)', borderRadius: '28px', padding: isMobile ? '1.75rem' : '3rem', position: 'relative', overflow: 'hidden', gridRow: isMobile ? 'auto' : 'span 2' }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: '60%', height: '100%', background: 'radial-gradient(ellipse at top right, rgba(252,76,2,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
              <p style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '1.5rem' }}>Training Stress Balance</p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '6rem', fontWeight: 900, lineHeight: 1, color: '#fc4c02' }}>-24</span>
                <span style={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.4)', marginBottom: '1rem' }}>TSB</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', marginBottom: '3rem' }}>Zona de fatiga acumulada. Carga alta sostenida los últimos 14 días.</p>
              <div style={{ height: '120px', display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
                {[30,45,20,60,35,70,55,40,65,80,50,75,60,45,35].map((h, i) => (
                  <div key={i} style={{ flex: 1, background: h > 65 ? '#fc4c02' : 'rgba(255,255,255,0.1)', height: h + '%', borderRadius: '3px 3px 0 0' }} />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                <span>14 días atrás</span><span>Hoy</span>
              </div>
            </div>

            {/* CARD 2: Readiness circular */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '28px', padding: isMobile ? '1.5rem' : '2.5rem' }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '1.5rem' }}>Readiness Score</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                <div style={{ position: 'relative', width: '110px', height: '110px', flexShrink: 0 }}>
                  <svg width="110" height="110" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="55" cy="55" r="48" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8"/>
                    <circle cx="55" cy="55" r="48" fill="none" stroke="#fc4c02" strokeWidth="8"
                      strokeDasharray="135.7 301.6"
                      strokeLinecap="round"/>
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '1.8rem', fontWeight: 900, color: '#fc4c02' }}>45</span>
                    <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>/100</span>
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', marginBottom: '0.5rem' }}>Carga alta.</p>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', lineHeight: 1.5 }}>Tu sistema nervioso aún procesa el bloque de intensidad. Prioriza recuperación activa.</p>
                </div>
              </div>
            </div>

            {/* CARD 3: Informe IA */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '28px', padding: isMobile ? '1.5rem' : '2.5rem' }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '1.5rem' }}>Informe IA · Esta semana</p>
              <div style={{ background: 'rgba(252,76,2,0.06)', border: '1px solid rgba(252,76,2,0.15)', borderRadius: '16px', padding: '1.5rem', fontFamily: 'monospace', fontSize: '0.9rem', lineHeight: 1.8 }}>
                <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>&gt; VFC nocturna: <span style={{ color: '#fc4c02' }}>54ms</span></div>
                <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>&gt; TSB acumulado: <span style={{ color: '#fc4c02' }}>-24</span></div>
                <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '1rem' }}>&gt; Sueño profundo: <span style={{ color: '#fc4c02' }}>1h 12m</span></div>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem', color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                  <strong style={{ color: 'white' }}>Diagnóstico:</strong> La fatiga es biológica, no mental. Reduce intensidad 2 días y el pico de forma llega el domingo.
                </div>
              </div>
            </div>

          </div>

          {/* FRANJA SYNC */}
          <div style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '28px', padding: '2rem 2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div>
              <p style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Ecosistema Conectado</p>
              <h3 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0 }}>Sincronización invisible. Sin fricción.</h3>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {['Strava', 'Garmin', 'Apple Health', 'Coros', 'Withings'].map((name, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', padding: '0.5rem 1.2rem', borderRadius: '40px', fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{name}</div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* NUEVAS FUNCIONES IA ÉLITE */}
      <section style={{ padding: '6rem 5%', background: '#050505' }}>
        <div style={{ maxWidth: '1300px', margin: '0 auto' }}>
          <div style={{ marginBottom: '4rem', textAlign: 'center' }}>
            <p style={{ color: '#00e5ff', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '1rem' }}>Súper-funciones de IA</p>
            <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, letterSpacing: '-0.02em', margin: 0, lineHeight: 1.1 }}>
              Mucho más que un resumen.<br/>
              <span style={{ background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Un fisiólogo en tu bolsillo.</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
            {/* Función 1 */}
            <div style={{ background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '24px', padding: '2.5rem' }}>
              <h3 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ background: 'rgba(239, 68, 68, 0.2)', padding: '0.5rem', borderRadius: '50%', color: '#ef4444', display: 'flex' }}><Flame size={24} /></span>
                Alertas Predictivas
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', lineHeight: 1.6 }}>Nuestra IA cruza caídas en tu VFC (HRV) con tu fatiga acumulada (TSB). Si el riesgo de lesión o enfermedad es crítico, recibes una alerta roja inmediata antes de entrenar.</p>
            </div>
            {/* Función 2 */}
            <div style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '24px', padding: '2.5rem' }}>
              <h3 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ background: 'rgba(16, 185, 129, 0.2)', padding: '0.5rem', borderRadius: '50%', color: '#10b981', display: 'flex' }}><Activity size={24} /></span>
                Nutrición Matemática
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', lineHeight: 1.6 }}>Se acabaron los consejos genéricos. Al analizar una sesión, el motor calcula los gramos exactos de carbohidratos (60%) y proteínas (20%) para recuperar tus calorías.</p>
            </div>
            {/* Función 3 */}
            <div style={{ background: 'rgba(0, 229, 255, 0.05)', border: '1px solid rgba(0, 229, 255, 0.2)', borderRadius: '24px', padding: '2.5rem' }}>
              <h3 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ background: 'rgba(0, 229, 255, 0.2)', padding: '0.5rem', borderRadius: '50%', color: '#00e5ff', display: 'flex' }}><BrainCircuit size={24} /></span>
                Contexto Climático
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', lineHeight: 1.6 }}>¿Por qué tus pulsaciones volaron ayer? Nos conectamos a satélites históricos para inyectar la temperatura local exacta de tu ruta y que la IA entienda el estrés térmico.</p>
            </div>
            {/* Función 4 */}
            <div style={{ background: 'rgba(168, 85, 247, 0.05)', border: '1px solid rgba(168, 85, 247, 0.2)', borderRadius: '24px', padding: '2.5rem' }}>
              <h3 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ background: 'rgba(168, 85, 247, 0.2)', padding: '0.5rem', borderRadius: '50%', color: '#a855f7', display: 'flex' }}><Watch size={24} /></span>
                Morning Briefing
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', lineHeight: 1.6 }}>Cada mañana, la IA lee tu sueño y tu fatiga y te saluda con un consejo proactivo de 1 sola frase. El estímulo exacto para salir a comerte el mundo, o quedarte en la cama.</p>
            </div>
          </div>
        </div>
      </section>

            {/* SUBSCRIPTION PLANS SOMA */}
      <section style={{ padding: '6rem 5%', background: '#0a0d14', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '3rem', fontWeight: 800, marginBottom: '1rem' }}>Desbloquea tu potencial.</h2>
          <p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.6)', marginBottom: '4rem' }}>Elige el plan que se adapte a tu nivel de exigencia.</p>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            {/* SOMA BASE (Freemium) */}
            <div style={{ flex: '1 1 300px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '24px', padding: '3rem 2rem', textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '0.5rem' }}>Basic</h3>
              <div style={{ fontSize: '3rem', fontWeight: 800, marginBottom: '0.25rem' }}>€0<span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }}>/mes</span></div>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', marginBottom: '2rem' }}>El panel de control esencial para atletas.</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '3rem', flex: 1, color: 'rgba(255,255,255,0.7)' }}>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}><span style={{color: '#fc4c02'}}>✓</span> Sincronización Strava / Garmin</li>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}><span style={{color: '#fc4c02'}}>✓</span> Gráficas PMC (CTL, ATL, TSB)</li>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}><span style={{color: '#fc4c02'}}>✓</span> Historial y Calendario Visual</li>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', opacity: 0.3 }}><span style={{color: '#888'}}>✕</span> Sin Inteligencia Artificial</li>
              </ul>
              <button onClick={() => navigate('/app')} style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '12px', fontWeight: 600, width: '100%', cursor: 'pointer' }}>Crear cuenta Gratis</button>
            </div>

            {/* SOMA PRO (IA ÉLITE) */}
            <div style={{ flex: '1 1 300px', background: 'rgba(252, 76, 2, 0.05)', border: '2px solid rgba(252, 76, 2, 0.5)', borderRadius: '24px', padding: '3rem 2rem', textAlign: 'left', display: 'flex', flexDirection: 'column', position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: '#fc4c02', color: 'white', padding: '4px 16px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '1px', border: '4px solid #000' }}>IA ÉLITE</div>
              <h3 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '0.5rem', color: '#fc4c02' }}>Atleta Pro</h3>
              <div style={{ fontSize: '3rem', fontWeight: 800, marginBottom: '0.25rem' }}><sup style={{ fontSize: '1.5rem', verticalAlign: 'top', marginRight: '4px' }}>€</sup>8,99<span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }}>/mes</span></div>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', marginBottom: '2rem' }}>El fisiólogo IA 24/7 en tu bolsillo.</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '3rem', flex: 1, color: 'rgba(255,255,255,0.9)' }}>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}><span style={{color: '#fc4c02'}}>✓</span> <strong>Todo lo de Basic</strong></li>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}><span style={{color: '#00e5ff'}}>✦</span> Análisis IA de cada sesión</li>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}><span style={{color: '#00e5ff'}}>✦</span> Prescripción Nutricional Exacta</li>
                <li style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}><span style={{color: '#ef4444'}}>✦</span> Alertas predictivas de lesión</li>
                <li style={{ display: 'flex', gap: '0.5rem' }}><span style={{color: '#a855f7'}}>✦</span> Briefing Matutino Proactivo</li>
              </ul>
              <button onClick={() => navigate('/app')} style={{ background: 'linear-gradient(135deg, #FC4C02 0%, #ff7b00 100%)', color: 'white', border: 'none', padding: '1rem', borderRadius: '12px', fontWeight: 800, width: '100%', cursor: 'pointer', boxShadow: '0 8px 20px rgba(252, 76, 2, 0.3)' }}>Desbloquear IA Élite</button>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION (Para garantizar acceso al Login) */}
      <section style={{ padding: '8rem 5%', background: '#000', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 1.5rem 0' }}>¿Listo para el siguiente nivel?</h2>
          <p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.6)', marginBottom: '3rem' }}>Únete a los atletas y entrenadores que ya están optimizando su rendimiento con datos precisos.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/app')} style={{ background: 'linear-gradient(135deg, #FC4C02 0%, #ff7b00 100%)', color: '#fff', border: 'none', padding: '1.2rem 2.5rem', borderRadius: '30px', fontWeight: 800, fontSize: '1.2rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.75rem', transition: 'transform 0.2s', boxShadow: '0 10px 30px rgba(252, 76, 2, 0.3)' }} onMouseOver={e => e.currentTarget.style.transform='translateY(-3px)'} onMouseOut={e => e.currentTarget.style.transform='translateY(0)'}>
              <LogIn size={24} /> Entrar a NousSoma
            </button>
          </div>
        </div>
      </section>

      {/* FAQ SOMA — específico para atletas */}
      <section style={{ padding: isMobile ? '4rem 6%' : '6rem 5%', background: '#050505', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <p style={{ color: '#fc4c02', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', textAlign: 'center', marginBottom: '1rem' }}>SOMA · ATLETAS</p>
          <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 800, textAlign: 'center', marginBottom: '0.75rem' }}>Todo lo que necesitas saber</h2>
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', marginBottom: '3rem', fontSize: '1rem' }}>Respuestas concretas para atletas que no pierden el tiempo.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

            <details className="faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(252,76,2,0.04)', border: '1px solid rgba(252,76,2,0.1)', borderRadius: '12px', fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Qué es el TSB y por qué importa más que el kilometraje?
                <ChevronDown className="faq-icon" size={18} style={{ flexShrink: 0, marginLeft: '0.5rem', color: '#fc4c02' }} />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                El <strong style={{color:'#fff'}}>TSB (Training Stress Balance)</strong> es la diferencia entre tu forma física acumulada (CTL) y tu fatiga reciente (ATL). Un TSB negativo alto significa que estás acumulando demasiada deuda de recuperación. NousSoma lo calcula automáticamente con cada actividad de Strava para decirte si debes apretar o aflojar, sin adivinanzas.
              </div>
            </details>

            <details className="faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(252,76,2,0.04)', border: '1px solid rgba(252,76,2,0.1)', borderRadius: '12px', fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Cómo sincronizo mis entrenamientos de Garmin o Apple Watch?
                <ChevronDown className="faq-icon" size={18} style={{ flexShrink: 0, marginLeft: '0.5rem', color: '#fc4c02' }} />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                NousSoma usa <strong style={{color:'#fff'}}>Strava como pasarela universal</strong>. Conectas tu cuenta de Strava una sola vez y todos tus dispositivos (Garmin, Coros, Suunto, Wahoo, Apple Watch) fluyen automáticamente hacia la plataforma. Además, la integración con <strong style={{color:'#fff'}}>Apple Health Shortcuts</strong> te permite subir datos de sueño y VFC directamente desde tu muñeca.
              </div>
            </details>

            <details className="faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(252,76,2,0.04)', border: '1px solid rgba(252,76,2,0.1)', borderRadius: '12px', fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Qué mide el Readiness Score exactamente?
                <ChevronDown className="faq-icon" size={18} style={{ flexShrink: 0, marginLeft: '0.5rem', color: '#fc4c02' }} />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                El Readiness Score (0–100) funciona como una batería en tiempo real. Cruza tres pilares matutinos: la <strong style={{color:'#fff'}}>variabilidad de frecuencia cardíaca (VFC)</strong>, la <strong style={{color:'#fff'}}>calidad del sueño</strong> y tu <strong style={{color:'#fff'}}>pulso en reposo</strong>. A lo largo del día, esta batería se recalcula restando el desgaste de tus entrenamientos. Si por la mañana marcas un 95 pero haces series de intensidad, tu batería bajará a un 45, avisándote de que necesitas descansar. Sin subjetividades, solo datos vivos.
              </div>
            </details>

            <details className="faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(252,76,2,0.04)', border: '1px solid rgba(252,76,2,0.1)', borderRadius: '12px', fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Puedo tener un entrenador y usar NousSoma a la vez?
                <ChevronDown className="faq-icon" size={18} style={{ flexShrink: 0, marginLeft: '0.5rem', color: '#fc4c02' }} />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                Completamente. NousSoma es la capa de datos del atleta. Si tu entrenador usa <strong style={{color:'#fff'}}>NousMentor</strong>, podrá acceder a tu panel en tiempo real y tomar decisiones con información objetiva. Si entrenas en solitario, NousSoma actúa como tu propio asistente técnico de bolsillo.
              </div>
            </details>

            <details className="faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(252,76,2,0.04)', border: '1px solid rgba(252,76,2,0.1)', borderRadius: '12px', fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Qué diferencia hay entre el plan Basic (Gratis) y Atleta Pro (€8,99)?
                <ChevronDown className="faq-icon" size={18} style={{ flexShrink: 0, marginLeft: '0.5rem', color: '#fc4c02' }} />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                El plan <strong style={{color:'#fff'}}>Basic (€0/mes)</strong> cubre la sincronización de actividades, tu historial completo, gráficas de carga PMC y métricas base. El plan <strong style={{color:'#fff'}}>Atleta Pro (€8,99/mes)</strong> desbloquea la Inteligencia Artificial Élite: alertas predictivas de lesión, nutrición matemática por sesión, contexto climático y tu briefing matutino.
              </div>
            </details>

          </div>
        </div>
      </section>

      {/* FOOTER PREMIUM */}
      <GlobalFooter />

      {/* ESTILOS DE ANIMACIÓN INYECTADOS */}
      <style>{`
        .bento-glass {
          background: rgba(20, 25, 40, 0.4);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 32px;
          opacity: 0;
          transform: translateY(40px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .bento-glass:hover {
          transform: scale(1.02) !important;
          border-color: rgba(255,255,255,0.1);
        }

        .bento-reveal {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }

        .bento-bg-gradient {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          z-index: 1;
          opacity: 0.5;
        }

        .cta-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 0 30px rgba(0, 229, 255, 0.6) !important;
        }

        .bar-anim {
          animation: growBar 2s ease-out forwards;
          transform-origin: bottom;
          transform: scaleY(0);
        }

        @keyframes growBar {
          to { transform: scaleY(1); }
        }

        .typing-anim {
          overflow: hidden;
          border-right: .15em solid #00e5ff;
          white-space: pre-wrap;
          margin: 0 auto;
          letter-spacing: .05em;
          animation: typing 4s steps(40, end), blink-caret .75s step-end infinite;
        }

        @keyframes typing {
          from { max-height: 0; opacity: 0; }
          to { max-height: 200px; opacity: 1; }
        }

        @keyframes blink-caret {
          from, to { border-color: transparent }
          50% { border-color: #fc4c02; }
        }

        .floating-logo {
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }

        .faq-item summary::-webkit-details-marker {
          display: none;
        }
        
        .faq-item[open] .faq-icon {
          transform: rotate(180deg);
        }
        
        .faq-icon {
          transition: transform 0.3s;
        }

        .badge-sync {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.8rem 1.5rem;
          border-radius: 20px;
          font-size: 1.2rem;
          font-weight: 700;
          color: rgba(255,255,255,0.9);
          backdrop-filter: blur(10px);
        }

        @media (max-width: 1000px) {
          .feature-block {
            flex-direction: column !important;
            height: auto !important;
            min-height: auto !important;
          }
          .feature-block.reverse {
            flex-direction: column-reverse !important;
          }
          .feature-image {
            width: 100% !important;
            height: 50vh !important;
          }
          .feature-text {
            padding: 4rem 2rem !important;
          }
        }
        
        @media (max-width: 900px) {
          .bento-grid {
            display: flex !important;
            flex-direction: column !important;
          }
          .bento-ai-anim {
            position: relative !important;
            width: 100% !important;
            right: 0 !important;
            transform: none !important;
            margin-top: 2rem;
          }
        }

        /* ====== MÓVIL ====== */
        @media (max-width: 768px) {
          nav { padding: 0.75rem 1rem !important; }
          
          /* Hero: reducir padding top para que el texto no quede tapado */
          section[style*="height: 100vh"] > div[style*="zIndex: 2"],
          section[style*="height: 100vh"] > div[style*="z-index: 2"] {
            margin-top: 0 !important;
            padding-top: 5rem;
          }

          /* Pricing cards: apilar */
          section div[style*="flex: '1 1 300px'"] { flex: 1 1 100% !important; }

          /* Feature blocks */
          .feature-block { flex-direction: column !important; min-height: auto !important; }
          .feature-block.reverse { flex-direction: column-reverse !important; }
          .feature-image { height: 45vw !important; min-height: 200px !important; }
          .feature-text { padding: 3rem 1.5rem !important; }

          /* Bento Grid */
          .bento-glass { min-height: auto !important; }
        }

        @media (max-width: 480px) {
          nav button { font-size: 0.75rem !important; padding: 0.3rem 0.6rem !important; }
        }
      `}</style>
    </div>
  );
}
